"use client";

import { AlertTriangle, Coins, Loader2 } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "@punchup/utils/dayjs";

import { Button } from "../../../shadcn/ui/button";
import { CreditTransaction, HistoryTabProps } from "./types";
import {
  getTransactionDollarAmount,
  getTransactionLabel,
  TransactionIcon,
} from "./transaction-helpers";

export function HistoryTab({
  onFetchTransactions,
  isDesktop,
  onSwitchTab,
}: HistoryTabProps) {
  const [transactions, setTransactions] = useState<CreditTransaction[] | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    setFetchError(null);
    onFetchTransactions()
      .then((data) => {
        if (mountedRef.current) {
          setTransactions(data);
          setFetchError(null);
        }
      })
      .catch((err) => {
        if (mountedRef.current) {
          setTransactions(null);
          setFetchError(
            err instanceof Error
              ? err.message
              : "Could not load your history. Check your connection and try again."
          );
        }
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
  }, [onFetchTransactions]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  const grouped = useMemo(() => {
    const result: Record<string, CreditTransaction[]> = {};
    if (transactions) {
      for (const txn of transactions) {
        const dateKey = dayjs(txn.created_at).format("MMMM D, YYYY");
        if (!result[dateKey]) result[dateKey] = [];
        result[dateKey].push(txn);
      }
    }
    return result;
  }, [transactions]);

  return (
    <div className="flex flex-col h-full">
      {isDesktop !== false && (
        <h2 className="text-lg font-semibold leading-[22px] text-primary-blue mb-[10px]">
          History
        </h2>
      )}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : fetchError ? (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 flex items-start gap-2 w-full max-w-sm">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm text-red-600">
              <p>{fetchError}</p>
              <button
                type="button"
                onClick={fetchData}
                className="underline text-sm text-red-600 mt-1"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      ) : !transactions || transactions.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-4">
          <Coins className="h-10 w-10 text-[#A4A6A8]" />
          <div>
            <p className="text-sm font-semibold text-[#29333C]">
              No transactions yet
            </p>
            <p className="text-sm text-[#787B7E] mt-1">
              Your credit purchases and usage will show up here.
            </p>
          </div>
          {isDesktop === false && onSwitchTab && (
            <Button
              variant="punchup"
              size="sm"
              onClick={() => onSwitchTab("buy-credits")}
              className="mt-2"
            >
              Buy Credits
            </Button>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto space-y-[10px] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {Object.entries(grouped).map(([date, txns]) => (
            <div key={date} className="space-y-[10px]">
              <p className="text-xs leading-6 font-normal text-[#787B7E]">
                {date}
              </p>
              {txns.map((txn) => {
                const dollarAmount = getTransactionDollarAmount(
                  txn.transaction_type,
                  txn.amount,
                  txn.amount_paid_cents,
                  txn.reference_type
                );
                return (
                  <div
                    key={txn.id}
                    className="flex items-start gap-[10px] rounded-[6px] border border-[#E7E7E7] bg-white px-4 py-3"
                  >
                    <TransactionIcon type={txn.transaction_type} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-[10px]">
                        <p className="text-sm leading-6 font-semibold text-[#29333C]">
                          {getTransactionLabel(
                            txn.transaction_type,
                            Math.abs(txn.amount),
                            txn.reference_type
                          )}
                        </p>
                        {dollarAmount && (
                          <p className="text-sm leading-6 font-normal text-[#29333C] shrink-0 text-right">
                            {dollarAmount}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
